import React from 'react';
import { Spinner } from 'reactstrap';
import styles from './UrlTracking.module.scss';
import { formatTimeFromSeconds } from '@/utils/timeUtils';
import { normalizeIdleForDisplay } from '@/utils/idleDisplayUtils';
import { ReportFiltersBar } from '@/components/ReportFiltersBar';
import { GaugeScoreCard } from '@/components/GaugeScoreCard';
import { TrendStatCard } from '@/components/TrendStatCard';
import { SessionAccordionCard } from '@/components/SessionAccordionCard';
import { getProductivityStatus } from '@/utils/productivityStatus';
import { DomainUsageTableComponent as DomainUsageTable } from './components/DomainUsageTable.component';
import { EmptyState } from '@/components/EmptyState';

// Pure presentational — no Redux/react-query/service calls. Rendered by both
// UrlTracking.component.jsx (real) and UrlTracking.demo.component.jsx
// (static fixture-backed); a future redesign only ever needs to touch this
// file plus whichever container's props it changes.
export const UrlTrackingView = ({
  membersOptions,
  selectedMember,
  onMemberFilterChange,
  membersLoading,
  membersError,
  membersErrorObj,
  refetchMembers,
  timezoneOptions,
  selectedTimezone,
  onTimezoneChange,
  timezonesLoading,
  date,
  onDateChange,
  onRefresh,
  loading,
  day,
  teamId,
  urlActivitiesBySessionId,
}) => {
  const dayTotals = day?.totals;
  const { displayIdle } = normalizeIdleForDisplay(
    dayTotals?.durationSec ?? 0,
    dayTotals?.idleSec ?? 0
  );
  const productivityStatus = getProductivityStatus(dayTotals?.activityPct ?? 0);

  return (
    <div className={styles.urlTrackingPage}>
      <ReportFiltersBar
        membersOptions={membersOptions}
        selectedMember={selectedMember}
        onMemberFilterChange={onMemberFilterChange}
        membersLoading={membersLoading}
        membersError={membersError}
        membersErrorObj={membersErrorObj}
        refetchMembers={refetchMembers}
        timezoneOptions={timezoneOptions}
        selectedTimezone={selectedTimezone}
        onTimezoneChange={onTimezoneChange}
        timezonesLoading={timezonesLoading}
        date={date}
        onDateChange={onDateChange}
        onRefresh={onRefresh}
        showExport={false}
      />

      {loading && (
        <div className={styles.loaderContainer}>
          <Spinner color="primary" />
          <p className="mt-3 text-muted">Loading sessions...</p>
        </div>
      )}

      {!loading && !day && (
        <EmptyState
          illustration
          title="No Sessions Found"
          description="No URL tracking sessions found for the selected filters."
        />
      )}

      {!loading && day && (
        <>
          <div className={styles.statsRow}>
            <GaugeScoreCard
              score={Number((dayTotals.activityPct ?? 0).toFixed(1))}
              label={productivityStatus.label}
              subtext={productivityStatus.subtext}
            />
            <TrendStatCard
              title="Total Time Tracked"
              tooltip="Total time tracked across all sessions today."
              value={formatTimeFromSeconds(dayTotals.durationSec)}
              tint="success-50"
              sparklineColor="success-500"
              // Placeholder — no hourly time-series exists in the data model
              // yet, so this sparkline is decorative, not real trend data.
              sparklineData={[4, 6, 5, 7, 6, 8, 7]}
            />
            <TrendStatCard
              title="Idle / Rest Time"
              tooltip="Time spent idle across all sessions today."
              value={formatTimeFromSeconds(displayIdle)}
              tint="error-50"
              sparklineColor="error-500"
              // Placeholder — see note above.
              sparklineData={[7, 6, 7, 5, 6, 4, 5]}
            />
          </div>

          <div className={styles.sessionsList}>
            {day.sessions.map((session, index) => (
              <SessionAccordionCard
                key={session.id}
                session={session}
                index={index}
                selectedTimezone={selectedTimezone}
              >
                <DomainUsageTable
                  session={session}
                  teamId={teamId}
                  userId={selectedMember?.value}
                  demoUrlActivities={urlActivitiesBySessionId?.[session.id]}
                />
              </SessionAccordionCard>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
